import dayjs from 'dayjs';

import {
    CommonPromotionStatusEnum,
    PromotionDetailType,
    PromotionFormType,
    PromotionRequestType,
} from '../../../service/promotions/promotions-type';
import {ProvidersIdsEnum} from '../../../util/type';
import {PublicationCompaniesSourceEnum} from '../../publication-wizard/publication-brand-or-companies-field/publication-brand-or-companies-helper';

// The design system's standard form-modal width, which is what the 520 in Figma rounds to.
export const PROMOTION_FORM_MODAL_WIDTH = 530;

// Catalogs are hidden from the UI for now — promotions always target Yandex.
const PROMOTION_CATALOG_IDS = [ProvidersIdsEnum.yandex];

export function getPromotionFormInitialValues(promotion: PromotionDetailType): PromotionFormType {
    return {
        title: promotion.title,
        description: promotion.description,
        url: promotion.url,
        images: promotion.images,
        period: [
            promotion.startAt ? dayjs(promotion.startAt) : null,
            promotion.finishAt ? dayjs(promotion.finishAt) : null,
        ],
        // Not editable any more, but carried through the form: the update endpoint assigns both
        // fields straight from the payload, so leaving them out would wipe the dates of promotions
        // created before the publication period was dropped from the UI.
        publishAt: promotion.publishAt ? dayjs(promotion.publishAt) : null,
        deletedAt: promotion.deletedAt ? dayjs(promotion.deletedAt) : null,
    };
}

// Drives the submit button: every required field has to be filled before the promotion can be sent.
// The photo and the URL are optional.
export function isPromotionFormFilled(
    values: PromotionFormType | undefined,
    companiesSource: PublicationCompaniesSourceEnum
): boolean {
    const isSelectionFilled =
        companiesSource === PublicationCompaniesSourceEnum.Brand
            ? Boolean(values?.brandId)
            : (values?.companiesCount ?? 0) > 0;

    // The period only counts as touched here. Both of its ends are checked by the field's own rule,
    // so a half-filled range has to reach validation instead of silently disabling submit.
    return Boolean(isSelectionFilled && values?.title?.trim() && values.description?.trim() && values.period);
}

export function preparePromotionRequestData(
    values: PromotionFormType,
    selectorId: string,
    status: CommonPromotionStatusEnum
): PromotionRequestType {
    const [startAt, finishAt] = values.period ?? [null, null];

    return {
        title: values.title,
        description: values.description ?? '',
        url: values.url ?? '',
        selectorId,
        catalogsIds: PROMOTION_CATALOG_IDS,
        // Not part of the form any more; an existing promotion keeps whatever it was created with.
        publishAt: values.publishAt ? values.publishAt.toISOString() : null,
        deletedAt: values.deletedAt ? values.deletedAt.toISOString() : null,
        // The picker is date-only, so the period spans whole days: from the first moment of the
        // start day to the last of the end day. The backend deactivates a promotion once `finish_at`
        // is in the past, so sending midnight would cut the last day off.
        startAt: startAt ? startAt.startOf('day').toISOString() : '',
        finishAt: finishAt ? finishAt.endOf('day').toISOString() : '',
        imageIds: (values.images ?? []).map((image) => image.id),
        status,
    };
}
